
import React, { useState, useEffect, Suspense, lazy, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useStore } from './context/StoreContext';
import { UserPermissions } from './types';
import Sidebar from './components/Sidebar';
import { BottomNav } from './components/BottomNav';
import { SplashScreen } from './components/SplashScreen';
import { PullToRefresh } from './components/PullToRefresh';
import Header from './components/Header';
import Login from './components/Login';
import { ErrorBoundary } from './components/ErrorBoundary';

import { Loader, CheckCircle, AlertCircle, Info, ArrowLeft, X } from 'lucide-react';
import { DashboardSkeleton, TableSkeleton } from './components/Skeleton';
import { motion, AnimatePresence } from 'motion/react';

// Lazy load components for better performance
const Dashboard = lazy(() => import('./components/Dashboard'));
const Agents = lazy(() => import('./components/Agents'));
const Warehouses = lazy(() => import('./components/Warehouses'));
const Products = lazy(() => import('./components/Products'));
const Orders = lazy(() => import('./components/Orders'));
const AllWarehouses = lazy(() => import('./components/AllWarehouses'));
const EmployeeRegistration = lazy(() => import('./components/EmployeeRegistration'));
const ApprovedInvoices = lazy(() => import('./components/ApprovedInvoices'));
const SystemMatch = lazy(() => import('./components/SystemMatch'));
const MatchInvoices = lazy(() => import('./components/MatchInvoices'));
const IntroVideo = lazy(() => import('./components/IntroVideo'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const InternalTransfers = lazy(() => import('./components/InternalTransfers'));
const SystemChat = lazy(() => import('./components/SystemChat'));
const CentersSystem = lazy(() => import('./components/CentersSystem'));
const CenterUsersManagement = lazy(() => import('./components/CenterUsersManagement'));
const CenterInterface = lazy(() => import('./components/CenterInterface'));
const DistinguishedUsers = lazy(() => import('./components/DistinguishedUsers'));
const PublicSalesPage = lazy(() => import('./components/PublicSalesPage'));
const OfferManagement = lazy(() => import('./components/OfferManagement'));
const Employees = lazy(() => import('./components/Employees'));
const Attendance = lazy(() => import('./components/Attendance'));
const DeferredPoints = lazy(() => import('./components/DeferredPoints'));
const Commissions = lazy(() => import('./components/Commissions'));
const ActivityLogView = lazy(() => import('./components/ActivityLogView'));
const MemberStatement = lazy(() => import('./components/MemberStatement'));
const CommissionDashboard = lazy(() => import('./components/CommissionDashboard'));
const CancelledSales = lazy(() => import('./components/CancelledSales'));
const DailyClosingsReports = lazy(() => import('./components/DailyClosingsReports'));
const StockForecasting = lazy(() => import('./components/StockForecasting'));
const CenterDashboard = lazy(() => import('./components/CenterDashboard'));
const CentersDashboard = lazy(() => import('./components/CentersDashboard'));
const UnifiedSalesTable = lazy(() => import('./components/UnifiedSalesTable'));
const Accounts = lazy(() => import('./components/Accounts'));
const ExchangeRateManagement = lazy(() => import('./components/ExchangeRateManagement'));
const Complaints = lazy(() => import('./components/Complaints'));
const PublicFeedbackPage = lazy(() => import('./components/PublicFeedbackPage'));
const WarehouseLocations = lazy(() => import('./components/WarehouseLocations'));
const CompanyLetters = lazy(() => import('./components/CompanyLetters'));
const EmployeePortal = lazy(() => import('./components/EmployeePortal'));
const CenterPortal = lazy(() => import('./components/CenterPortal'));
const ProductSalesTracker = lazy(() => import('./components/ProductSalesTracker'));

const SystemSettings = lazy(() => import('./components/SystemSettings'));

const EmployeeSelfRegistration = lazy(() => import('./components/EmployeeSelfRegistration'));

const Unauthorized = () => (
  <div className="p-8 flex flex-col items-center justify-center h-[calc(100vh-100px)] text-gray-400">
    <div className="text-6xl mb-4 text-rose-400 animate-pulse">⛔</div>
    <h2 className="text-2xl font-black mb-2 text-slate-800">عذراً، غير مصرح لك بالدخول</h2>
    <p className="font-bold text-slate-500">ليس لديك صلاحية للوصول إلى هذه الصفحة.</p>
  </div>
);

const ProtectedRoute = ({ 
  children, 
  permissionKey, 
  routeId 
}: { 
  children: React.ReactNode; 
  permissionKey?: keyof UserPermissions;
  routeId?: string;
}) => {
  const { currentUser } = useStore();
  
  if (!currentUser) return <Navigate to="/" replace />;
  
  const isGlobalManager = currentUser?.permissions?.manageSettings;
  const isRestricted = !!currentUser?.agentId && !isGlobalManager;
  const allowedForRestricted = [
    'dashboard', 'warehouses', 'centers_dashboard', 'unified_sales', 
    'warehouses_dashboard', 'center_dashboard', 'accounts', 'orders', 
    'products', 'centers_system', 'center_interface', 'offers', 
    'attendance', 'commissions', 'distinguished_users', 'points', 
    'deferred_invoices', 'gifts', 'transfers', 'all_warehouses', 'complaints',
    'system_match', 'match_invoices', 'stock_forecasting', 'commissions', 'points',
    'product_sales_tracker'
  ];

  if (permissionKey && !currentUser.permissions[permissionKey]) {
    return <Unauthorized />;
  }

  // If the user has explicitly been granted the permission for this route, allow it
  if (permissionKey && currentUser.permissions[permissionKey]) {
    return <>{children}</>;
  }

  if (isRestricted && routeId && !allowedForRestricted.includes(routeId)) {
    return <Unauthorized />;
  }

  return <>{children}</>;
};

const PlaceholderComponent: React.FC<{ title: string }> = ({ title }) => (
  <div className="p-8 flex flex-col items-center justify-center h-[calc(100vh-100px)] text-gray-400">
    <div className="text-6xl mb-4 text-orange-400 animate-bounce">🚧</div>
    <h2 className="text-2xl font-black mb-2 text-gray-800">{title}</h2>
    <p className="font-bold">هذه الصفحة قيد التطوير حالياً</p>
  </div>
);

const Toast: React.FC = () => {
  const { toast } = useStore();

  const bgClass = toast ? {
    success: 'bg-emerald-900/95 text-emerald-100 border-emerald-500/30 shadow-[0_4px_30px_rgba(16,185,129,0.35)] backdrop-blur-md',
    error: 'bg-rose-900/95 text-rose-100 border-rose-500/30 shadow-[0_4px_30px_rgba(244,63,94,0.35)] backdrop-blur-md',
    info: 'bg-indigo-900/95 text-indigo-100 border-indigo-500/30 shadow-[0_4px_30px_rgba(99,102,241,0.35)] backdrop-blur-md'
  }[toast.type] : '';

  const iconColor = toast ? {
    success: 'text-emerald-400',
    error: 'text-rose-400',
    info: 'text-indigo-400'
  }[toast.type] : '';

  const Icon = toast ? {
    success: CheckCircle,
    error: AlertCircle,
    info: Info
  }[toast.type] : CheckCircle;

  return (
    <AnimatePresence>
      {toast && (
        <motion.div 
          initial={{ opacity: 0, y: -40, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 450, damping: 28 }}
          className="fixed top-6 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{ zIndex: 9999999 }}
        >
          <div className={`${bgClass} px-6 py-4 rounded-[1.25rem] border flex items-center gap-3.5 min-w-[320px] max-w-[90vw] pointer-events-auto transition-transform active:scale-98`}>
            <div className={`p-1 rounded-lg ${toast.type === 'success' ? 'bg-emerald-500/15' : toast.type === 'error' ? 'bg-rose-500/15' : 'bg-indigo-500/15'}`}>
              <Icon size={20} className={`${iconColor} shrink-0`} style={{ strokeWidth: 2.5 }} />
            </div>
            <span className="font-bold text-sm tracking-wide text-white flex-1 leading-relaxed">{toast.message}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ProcessingOverlay: React.FC = () => {
  const { isProcessing } = useStore();
  
  return (
    <AnimatePresence>
      {isProcessing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="fixed inset-0 flex items-center justify-center bg-slate-950/45 backdrop-blur-[3px] cursor-wait"
          style={{ zIndex: 9999998 }}
        >
          <div className="bg-white p-7 rounded-[2rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.2)] border border-gray-100/80 flex flex-col items-center gap-5 scale-100">
            <div className="relative">
              <div 
                className="w-14 h-14 border-[3.5px] border-indigo-100 border-t-indigo-600 rounded-full animate-spin" 
                style={{ animationDuration: '0.45s' }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-ping" style={{ animationDuration: '0.8s' }}></div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <p className="text-lg font-black text-slate-800">جاري الحفظ...</p>
              <p className="text-[10px] font-bold text-slate-400">يرجى الانتظار لحظة</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const WelcomePortal: React.FC = () => {
  const { welcomeUser, setWelcomeUser } = useStore();
  const navigate = useNavigate();
  
  if (!welcomeUser) return null;

  const handleAction = (path: string | null) => {
    if (path) navigate(path);
    setWelcomeUser(null);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6"
      >
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-slate-900/60"
          onClick={() => setWelcomeUser(null)}
        />
        
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 10 }}
          transition={{ type: "tween", duration: 0.2, ease: "easeOut" }}
          className="bg-white w-full max-w-[480px] rounded-[3.5rem] p-10 md:p-14 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] relative overflow-hidden text-center border border-white"
        >
          {/* Background Decorative Element */}
          <div className="absolute top-0 left-0 w-full h-[6px] bg-gradient-to-r from-blue-600 via-indigo-600 to-indigo-400" />
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-50" />
          
          <button 
            onClick={() => setWelcomeUser(null)}
            className="absolute top-8 left-8 p-3 text-slate-300 hover:text-slate-900 hover:bg-slate-50 rounded-2xl transition-all"
          >
            <X size={24} strokeWidth={2.5} />
          </button>

          <motion.div 
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 15, delay: 0.2 }}
            className="w-28 h-28 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 relative shadow-inner"
          >
            <motion.div 
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0, 0.5, 0]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 bg-blue-400 rounded-[2.5rem]" 
            />
            <span className="text-5xl drop-shadow-sm">👋</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-4 mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[11px] font-black uppercase tracking-widest mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
              دخول آمن
            </div>
            <h2 className="text-[2.75rem] font-black text-slate-900 leading-tight font-display tracking-tight">
              أهلاً بك، <br />
              <span className="text-blue-600">{welcomeUser.name}</span>
            </h2>
            <p className="text-slate-400 font-bold text-lg max-w-[300px] mx-auto leading-relaxed">
              يسعدنا تواجدك معنا في نظام رائد الخير المتكامل
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-5"
          >
            {/* Primary Action: Attendance Reminder */}
            <button
              onClick={() => handleAction('/attendance')}
              className="w-full h-20 bg-slate-900 text-white rounded-[2rem] font-black text-xl flex items-center justify-between px-10 shadow-[0_20px_40px_-5px_rgba(15,23,42,0.3)] hover:bg-black transition-all hover:scale-[1.02] active:scale-95 group"
            >
              <div className="flex flex-col items-start translate-y-[1px]">
                <span className="text-xs text-white/40 uppercase tracking-widest font-space mb-0.5">Required Step</span>
                <span>تسجيل الحضور اليومي</span>
              </div>
              <ArrowLeft size={24} className="text-blue-400 group-hover:-translate-x-2 transition-transform" />
            </button>

            {/* Secondary Action: Dashboard */}
            <button
              onClick={() => handleAction(null)}
              className="w-full h-18 bg-white border-2 border-slate-100 text-slate-500 rounded-[2rem] font-black text-lg flex items-center justify-center gap-3 hover:bg-slate-50 hover:border-slate-200 transition-all active:scale-95 transition-all"
            >
              <span>تخطي للوحة التحكم</span>
            </button>
            
            <p className="text-[9px] text-slate-300 font-black uppercase tracking-[0.4em] font-space pt-4">
              Enterprise Grade Logistics v4.3
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


const DatabaseStatusBanner: React.FC = () => {
  const [dbInfo, setDbInfo] = useState<{ database: string, tip: string } | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch('/api/health');
        const text = await res.text();
        
        if (text.includes('<title>Starting Server...</title>') || text.includes('Rate exceeded')) {
          setDbInfo({ database: "جاري تشغيل النظام...", tip: "النظام في مرحلة التفعيل وتهيئة الاتصال، يرجى الانتظار ثواني." });
          return;
        }

        const data = JSON.parse(text);
        if (data.database && data.database.includes('disconnected')) {
          let tip = data.tip;
          if (data.database.includes('ECONNREFUSED') || data.database.includes('ENOTFOUND') || data.database.includes('ETIMEDOUT')) {
            tip = "السيرفر (62.171.179.216) لا يستجيب للاتصال. يرجى تفعيل Remote MySQL في cPanel وإضافة IP التطبيق: 34.34.246.216";
          }
          setDbInfo({ database: "قاعدة البيانات غير متصلة", tip: tip });
        } else {
          setDbInfo(null);
        }
      } catch (e) {
        // Silent
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!dbInfo) return null;

  return (
    <div className="bg-[#0f172a] text-white px-4 py-2.5 text-[10px] font-black flex items-center justify-between gap-4 no-print border-b border-white/5 uppercase tracking-widest">
      <div className="flex items-center gap-3">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
        <span>System Status: {dbInfo.database}</span>
      </div>
      <div className="flex-1 text-center hidden md:block opacity-80">
        {dbInfo.tip}
      </div>
      <button 
        onClick={() => setDbInfo(null)}
        className="p-1 hover:bg-white/10 rounded transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const App: React.FC = () => {
  const { currentUser, isAuthenticated, loading, viewInvoiceId, systemSettings, fetchData } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  
  useEffect(() => {
    // Hide splash screen after 1.5 seconds or when loading finishes
    if (!loading) {
      const timer = setTimeout(() => setShowSplash(false), 800);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const [isPublicBuy, setIsPublicBuy] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'buy';
  });
  const [isPublicFeedback, setIsPublicFeedback] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'feedback';
  });
  const [buyCenterId, setBuyCenterId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('centerId');
  });
  const [feedbackCenterId, setFeedbackCenterId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('centerId');
  });

  const activeTab = useMemo(() => {
    const path = location.pathname.substring(1);
    if (path === 'register-employee') return 'register-employee';
    return path || 'dashboard';
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'buy') {
      setIsPublicBuy(true);
      setBuyCenterId(params.get('centerId'));
    }
    if (params.get('view') === 'feedback') {
      setIsPublicFeedback(true);
      setFeedbackCenterId(params.get('centerId'));
    }
    if (params.get('view') === 'register') {
      navigate('/register-employee');
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (viewInvoiceId) {
      navigate('/orders');
    }
  }, [viewInvoiceId, navigate]);

  useEffect(() => {
    // Ensure scroll to top on path change
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading && showSplash) {
    return <SplashScreen isLoading={true} logoUrl={systemSettings?.loading_logo_url} />;
  }

  // Handle Public Routes Before Login Check
  if (location.pathname === '/register-employee') {
    return (
      <div className="min-h-screen bg-gray-50 font-sans" dir="rtl">
        <Suspense fallback={<DashboardSkeleton />}>
          <EmployeeRegistration />
        </Suspense>
        <Toast />
      </div>
    );
  }

  if (location.pathname === '/staff-setup') {
    return (
      <div className="min-h-screen font-sans" dir="rtl">
        <Suspense fallback={<DashboardSkeleton />}>
          <EmployeeSelfRegistration />
        </Suspense>
        <Toast />
      </div>
    );
  }

  if (isPublicBuy && buyCenterId) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans" dir="rtl">
        <PublicSalesPage centerId={buyCenterId} />
        <Toast />
      </div>
    );
  }

  if (location.pathname.startsWith('/employee/')) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans" dir="rtl">
        <Suspense fallback={<TableSkeleton />}>
          <Routes location={location}>
            <Route path="/employee/:username" element={<EmployeePortal />} />
          </Routes>
        </Suspense>
        <Toast />
      </div>
    );
  }

  if (location.pathname.startsWith('/center/')) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans" dir="rtl">
        <Suspense fallback={<TableSkeleton />}>
          <Routes location={location}>
            <Route path="/center/:centerName" element={<CenterPortal />} />
          </Routes>
        </Suspense>
        <Toast />
      </div>
    );
  }

  if (isPublicFeedback && feedbackCenterId) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans" dir="rtl">
        <PublicFeedbackPage centerId={feedbackCenterId} />
        <Toast />
      </div>
    );
  }

  if (!currentUser && !isAuthenticated) {
    return <Login />;
  }

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="min-h-screen bg-[#f1f5f9] text-gray-900 overflow-x-hidden relative transition-colors duration-200 font-sans" dir="rtl">
      <DatabaseStatusBanner />
      <SplashScreen isLoading={showSplash} logoUrl={systemSettings?.loading_logo_url} />
      
      {/* Structural Grid Background - Mission Control Aesthetic */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.03]" 
           style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #1e293b 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      
      {/* Premium Gradient Blobs */}
      <div className="fixed top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full blur-[150px] pointer-events-none transition-colors duration-500 bg-blue-500/10 mix-blend-multiply" />
      <div className="fixed bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full blur-[150px] pointer-events-none transition-colors duration-500 bg-indigo-500/10 mix-blend-multiply" />
      <div className="fixed top-[30%] right-[10%] w-[30%] h-[30%] rounded-full blur-[120px] pointer-events-none transition-colors duration-500 bg-emerald-500/5 mix-blend-multiply" />
      
      <WelcomePortal />
      <ProcessingOverlay />
      <div className="no-print">
        <Sidebar 
          activeTab={activeTab} 
          isOpen={isSidebarOpen} 
          onClose={() => setIsSidebarOpen(false)} 
        />
      </div>
      
      <div className={`transition-all duration-200 min-h-screen flex flex-col ${isSidebarOpen ? 'lg:mr-80' : 'mr-0 lg:mr-80'} pb-24 lg:pb-0`}>
        <div className="no-print">
          <Header toggleSidebar={toggleSidebar} />
        </div>
        
        <main className="flex-1 flex flex-col px-4 sm:px-6 md:px-8 py-4 lg:py-6 relative max-w-[1920px] mx-auto w-full overflow-hidden">
          <PullToRefresh onRefresh={async () => {
            const tableMap: Record<string, string> = {
              'dashboard': 'products',
              'orders': 'invoices',
              'products': 'product_invoices'
            };
            const currentTab = location.pathname.substring(1) || 'dashboard';
            await fetchData(true, tableMap[currentTab] || undefined);
          }}>
            <AnimatePresence mode="wait">
              <motion.div 
                key={location.pathname}
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.12, ease: "easeOut" }}
                className="w-full"
              >
                <ErrorBoundary key={location.pathname}>
                  <Suspense fallback={<TableSkeleton />}>
                    <Routes location={location}>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<ProtectedRoute routeId="dashboard"><Dashboard /></ProtectedRoute>} />
                      <Route path="/centers_dashboard" element={<ProtectedRoute permissionKey="viewCentersDashboard" routeId="centers_dashboard"><CentersDashboard /></ProtectedRoute>} />
                      <Route path="/center_dashboard" element={<ProtectedRoute permissionKey="viewCenterInterface" routeId="center_dashboard"><CenterDashboard /></ProtectedRoute>} />
                      <Route path="/accounts" element={<ProtectedRoute permissionKey="viewAccounts" routeId="accounts"><Accounts /></ProtectedRoute>} />
                      <Route path="/exchange_rate" element={<ProtectedRoute permissionKey="manageExchangeRate" routeId="exchange_rate"><ExchangeRateManagement /></ProtectedRoute>} />
                      <Route path="/agents" element={<ProtectedRoute permissionKey="viewAgents" routeId="agents"><Agents /></ProtectedRoute>} />
                      <Route path="/orders" element={<ProtectedRoute permissionKey="viewOrders" routeId="orders"><Orders /></ProtectedRoute>} />
                      <Route path="/transfers" element={<ProtectedRoute permissionKey="viewTransfers" routeId="transfers"><InternalTransfers /></ProtectedRoute>} />
                      <Route path="/products" element={<ProtectedRoute permissionKey="viewProducts" routeId="products"><Products /></ProtectedRoute>} />
                      <Route path="/warehouses" element={<ProtectedRoute permissionKey="viewWarehousesDashboard" routeId="warehouses"><Warehouses /></ProtectedRoute>} />
                      <Route path="/product_sales_tracker" element={<ProtectedRoute permissionKey="viewWarehousesDashboard" routeId="product_sales_tracker"><ProductSalesTracker /></ProtectedRoute>} />
                      <Route path="/daily_closings" element={<ProtectedRoute permissionKey="viewDailyClosings" routeId="daily_closings"><DailyClosingsReports /></ProtectedRoute>} />
                      <Route path="/all_warehouses" element={<ProtectedRoute permissionKey="viewAllWarehouses" routeId="all_warehouses"><AllWarehouses /></ProtectedRoute>} />
                      <Route path="/unified_sales" element={<ProtectedRoute permissionKey="viewUnifiedSales" routeId="unified_sales"><UnifiedSalesTable /></ProtectedRoute>} />
                      <Route path="/approved_invoices" element={<ProtectedRoute permissionKey="viewApprovedInvoices" routeId="approved_invoices"><ApprovedInvoices /></ProtectedRoute>} />
                      <Route path="/system_match" element={<ProtectedRoute permissionKey="viewSystemMatch" routeId="system_match"><SystemMatch /></ProtectedRoute>} />
                      <Route path="/match_invoices" element={<ProtectedRoute permissionKey="viewMatchInvoices" routeId="match_invoices"><MatchInvoices /></ProtectedRoute>} />
                      <Route path="/intro_video" element={<ProtectedRoute permissionKey="viewIntroVideo" routeId="intro_video"><IntroVideo /></ProtectedRoute>} />
                      <Route path="/users" element={<ProtectedRoute permissionKey="viewUsers" routeId="users"><UserManagement /></ProtectedRoute>} />
                      <Route path="/centers_system" element={<ProtectedRoute permissionKey="viewCentersSystem" routeId="centers_system"><CentersSystem /></ProtectedRoute>} />
                      <Route path="/centers_system/:centerName" element={<ProtectedRoute permissionKey="viewCentersSystem" routeId="centers_system"><CentersSystem /></ProtectedRoute>} />
                      <Route path="/employees" element={<ProtectedRoute permissionKey="viewEmployees" routeId="employees"><Employees /></ProtectedRoute>} />
                      <Route path="/center_users" element={<ProtectedRoute permissionKey="viewCenterUsers" routeId="center_users"><CenterUsersManagement /></ProtectedRoute>} />
                      <Route path="/distinguished_users" element={<ProtectedRoute permissionKey="viewDistinguishedUsers" routeId="distinguished_users"><DistinguishedUsers /></ProtectedRoute>} />
                      <Route path="/center_interface" element={<ProtectedRoute permissionKey="viewCenterInterface" routeId="center_interface"><CenterInterface /></ProtectedRoute>} />
                      <Route path="/cancelled_sales" element={<ProtectedRoute permissionKey="viewCancelledSales" routeId="cancelled_sales"><CancelledSales /></ProtectedRoute>} />
                      <Route path="/offers" element={<ProtectedRoute permissionKey="viewOffers" routeId="offers"><OfferManagement centerId={currentUser?.agentId || ''} type="offer" /></ProtectedRoute>} />
                      <Route path="/deferred_invoices" element={<ProtectedRoute permissionKey="viewOffers" routeId="deferred_invoices"><OfferManagement centerId={currentUser?.agentId || ''} type="deferred" /></ProtectedRoute>} />
                      <Route path="/gifts" element={<ProtectedRoute permissionKey="viewOffers" routeId="gifts"><OfferManagement centerId={currentUser?.agentId || ''} type="gift" /></ProtectedRoute>} />
                      <Route path="/attendance" element={<ProtectedRoute permissionKey="viewAttendance" routeId="attendance"><Attendance /></ProtectedRoute>} />
                      <Route path="/warehouse_locations" element={<ProtectedRoute permissionKey="viewWarehouseLocations" routeId="warehouse_locations"><WarehouseLocations /></ProtectedRoute>} />
                      <Route path="/complaints" element={<ProtectedRoute permissionKey="viewComplaints" routeId="complaints"><Complaints /></ProtectedRoute>} />
                      <Route path="/company_letters" element={<ProtectedRoute permissionKey="viewCompanyLetters" routeId="company_letters"><CompanyLetters /></ProtectedRoute>} />
                      <Route path="/stock_forecasting" element={<ProtectedRoute permissionKey="viewStockForecasting" routeId="stock_forecasting"><StockForecasting /></ProtectedRoute>} />
                      <Route path="/commissions" element={<ProtectedRoute permissionKey="viewCommissions" routeId="commissions"><Commissions /></ProtectedRoute>} />
                      <Route path="/commission_dashboard" element={<ProtectedRoute routeId="commission_dashboard"><CommissionDashboard /></ProtectedRoute>} />
                      <Route path="/member_statement" element={<ProtectedRoute routeId="member_statement"><MemberStatement /></ProtectedRoute>} />
                      <Route path="/activity_log" element={<ProtectedRoute permissionKey="viewActivityLog" routeId="activity_log"><ActivityLogView /></ProtectedRoute>} />
                      <Route path="/points" element={<ProtectedRoute permissionKey="viewPoints" routeId="points"><DeferredPoints /></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute permissionKey="manageSettings" routeId="settings"><SystemSettings /></ProtectedRoute>} />
                      <Route path="*" element={<PlaceholderComponent title="صفحة غير موجودة" />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </motion.div>
            </AnimatePresence>
          </PullToRefresh>
        </main>
        
        <footer className="no-print hidden lg:block p-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] bg-white border-t border-gray-100 relative transition-colors shrink-0">
          &copy; 2026  جميع الحقوق محفوظة لشركة رائد الخير 
        </footer>
      </div>
      
      <div className="no-print">
        <BottomNav 
          activeTab={activeTab} 
          onMenuClick={toggleSidebar}
          currentUser={currentUser}
        />
      </div>

      <Suspense fallback={null}>
        <SystemChat />
      </Suspense>
      <Toast />
    </div>
  );
};

export default App;
